import { normalizePhoneCountryBR } from "@/utils/phone";
import { createContext, useContext, useEffect, useRef, useState, useCallback, type ReactNode } from "react";
import { desligarClienteMotor, registrarFalhaSoftphone, getMeuSip, getRamaisInternos, getPresenca, getGravacoes, getGravacaoUrl, type RamalInterno, type Gravacao } from "@/lib/softphoneApi";
import { toast } from "sonner";
import { identificarNumero } from "@/lib/identificarNumero";
import { COORDENA_ABAS, criarCanal, disputarPosse, type SoftphoneCmd, type SoftphoneEstado, type SoftphoneMsg } from "@/lib/softphoneBus";

type SoftphoneStatus = "carregando" | "conectando" | "registrado" | "desconectado" | "falha" | "sem-ramal";

interface SoftphoneApi {
  status: SoftphoneStatus;
  open: boolean;
  setOpen: (v: boolean) => void;
  motorSalaAtiva: boolean;
  setMotorSalaAtiva: (v: boolean) => void;
  inCall: boolean;
  /** ⛔ Feedback imediato do clique em Desligar — ver softphoneBus.ts. */
  encerrando: boolean;
  muted: boolean;
  number: string;
  setNumber: (v: string) => void;
  contactName: string | null;
  contactFoto: string | null;
  calling: boolean;
  ramal: string | null;
  timer: string;
  ramais: RamalInterno[] | null;
  carregarRamais: () => void;
  presenca: string[];
  carregarPresenca: () => void;
  historico: Gravacao[] | null;
  carregarHistorico: () => void;
  urlGravacao: (id: string) => Promise<string | null>;
  incoming: boolean;
  incomingFrom: string | null;
  answerCall: () => void;
  rejectCall: () => void;
  call: (numero: string, nome?: string, foto?: string | null) => void;
  transfer: (target: string) => void;
  hangup: () => void;
  toggleMute: () => void;
  sendDTMF: (d: string) => void;
}

const AUDIO_CONSTRAINTS = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };
const PC_CONFIG = { iceServers: [{ urls: "stun:stun.l.google.com:19302" }] };

/** Depois deste tempo sem notícia da aba dona, paramos de mostrar o estado dela. */
const ESTADO_VENCE_MS = 12_000;
/** De quanto em quanto a dona repete o estado, para as outras saberem que ela vive. */
const BATIDA_MS = 3_000;
const MESA_ATIVA_KEY = "discadora_mesa_ativa";

const ehMesa = typeof window !== "undefined" && window.location.pathname.startsWith("/mesa-prime-app");

function mesaEstaAberta(): boolean {
  try {
    const atual = JSON.parse(localStorage.getItem(MESA_ATIVA_KEY) || "null");
    return !!atual?.at && Date.now() - Number(atual.at) < 6_000;
  } catch {
    return false;
  }
}

/**
 * Motor de áudio do softphone (sintetizado — nada de arquivo de terceiros):
 *  - ringtone: marimba pra quem RECEBE a chamada
 *  - ringback: tom 425Hz (chamando…) pra quem LIGA
 *  - unlock(): destrava o AudioContext no 1º gesto (navegador bloqueia áudio sem interação)
 * Um único AudioContext persistente (não fecha), reaproveitado.
 */
const sfAudio = (() => {
  const NOTES = [440, 554.37, 659.25, 880, 659.25, 554.37]; // arpejo de Lá maior (marimba)
  let ctx: AudioContext | null = null;
  let ringtoneTimer: ReturnType<typeof setTimeout> | null = null;
  let ringbackTimer: ReturnType<typeof setTimeout> | null = null;
  let ringtoneOn = false;
  let ringbackOn = false;

  const ensure = (): AudioContext | null => {
    try {
      if (!ctx) {
        const AC = window.AudioContext || (window as any).webkitAudioContext;
        if (!AC) return null;
        ctx = new AC();
      }
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
    } catch { return null; }
    return ctx;
  };

  const marimbaNote = (freq: number, t: number) => {
    if (!ctx) return;
    const o = ctx.createOscillator();
    const o2 = ctx.createOscillator();
    const g = ctx.createGain();
    const g2 = ctx.createGain();
    o.type = "triangle"; o.frequency.value = freq;
    o2.type = "sine"; o2.frequency.value = freq * 2;
    o.connect(g); o2.connect(g2);
    g.connect(ctx.destination); g2.connect(ctx.destination);
    const A = 0.004, D = 0.22;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.26, t + A);
    g.gain.exponentialRampToValueAtTime(0.0008, t + D);
    g2.gain.setValueAtTime(0.0001, t);
    g2.gain.exponentialRampToValueAtTime(0.07, t + A);
    g2.gain.exponentialRampToValueAtTime(0.0006, t + D);
    o.start(t); o.stop(t + D + 0.02);
    o2.start(t); o2.stop(t + D + 0.02);
  };

  const ringtoneBurst = () => {
    if (!ctx || !ringtoneOn) return;
    const now = ctx.currentTime;
    const step = 0.12;
    NOTES.forEach((n, i) => marimbaNote(n, now + i * step));
    ringtoneTimer = setTimeout(ringtoneBurst, (NOTES.length * step + 0.55) * 1000);
  };

  const ringbackCycle = () => {
    if (!ctx || !ringbackOn) return;
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine"; o.frequency.value = 425; // tom de controle de chamada (BR)
    o.connect(g); g.connect(ctx.destination);
    const ON = 1.0;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.15, t + 0.04);
    g.gain.setValueAtTime(0.15, t + ON - 0.06);
    g.gain.exponentialRampToValueAtTime(0.0001, t + ON);
    o.start(t); o.stop(t + ON + 0.03);
    ringbackTimer = setTimeout(ringbackCycle, (ON + 2.6) * 1000); // cadência ~1s liga / 2.6s desliga
  };

  return {
    unlock() { ensure(); },
    startRingtone() { if (ringtoneOn) return; if (!ensure()) return; ringtoneOn = true; ringtoneBurst(); },
    stopRingtone() { ringtoneOn = false; if (ringtoneTimer) { clearTimeout(ringtoneTimer); ringtoneTimer = null; } },
    startRingback() { if (ringbackOn) return; if (!ensure()) return; ringbackOn = true; ringbackCycle(); },
    stopRingback() { ringbackOn = false; if (ringbackTimer) { clearTimeout(ringbackTimer); ringbackTimer = null; } },
  };
})();

const SoftphoneContext = createContext<SoftphoneApi | null>(null);

export function useSoftphone(): SoftphoneApi {
  const ctx = useContext(SoftphoneContext);
  if (!ctx) throw new Error("useSoftphone precisa do SoftphoneProvider");
  return ctx;
}

/** Retorna o softphone se disponível (provider montado), senão null — seguro fora do provider. */
export function useSoftphoneOptional(): SoftphoneApi | null {
  return useContext(SoftphoneContext);
}

function normalize(raw: string): string {
  return normalizePhoneCountryBR(raw);
}

/**
 * Desliga a perna do CLIENTE na sala da discadora, avisando quando não deu.
 *
 * `desligarClienteMotor` devolve `false` quando o pedido NÃO chegou ao servidor
 * (500, sessão expirada, rede caída). Antes isto era chamado com `void`, que
 * jogava a resposta fora: falha ficava idêntica a sucesso na tela, o consultor
 * ia para o próximo cliente e a perna do anterior seguia no ar — exatamente o
 * buraco que a correção da função tinha acabado de fechar (Douglas 04/09/2026).
 */
function desligarClienteMotorAvisando() {
  desligarClienteMotor().then((r) => {
    if (r.ok) return;
    // Dois motivos, dois conselhos opostos: com o motor parado, "tente de novo"
    // falharia pra sempre — o certo e' parar e avisar o suporte. (04/09/2026)
    toast.error("O cliente pode NÃO ter sido desligado", {
      description: r.motorForaDoAr
        ? "O motor da discadora está fora do ar. Avise o suporte — tentar de novo não vai adiantar."
        : "O pedido não chegou ao servidor. Confira a sala da discadora e tente de novo.",
      duration: 9000,
    });
  });
}

/**
 * O softphone da Prime — UM por navegador, visível e comandável de TODAS as abas.
 *
 * ── Por que existe a figura da "aba dona" (Douglas 09/08/2026) ──────────────
 * "Se tiver mil telas abertas, ele tem que entender que a ligação e o softphone
 * têm que estar preparados: eu aperto o botão de ligar num número na tela e ele
 * faz a ligação. Nunca sairá duas ligações ao mesmo tempo, independente de
 * quantas abas estiver."
 *
 * O Asterisk só aceita um registro por ramal (max_contacts=1 + remove_existing=yes).
 * Antes, cada aba registrava o mesmo ramal e chutava a anterior — 35.115 pares de
 * sessões sobrepostas no log, pico de 25 abas num ramal só, e a aba chutada
 * levava de 5 a 10 minutos para voltar (mediana medida: 429s).
 *
 * Agora UMA aba segura o registro e as outras espelham o estado dela e mandam
 * comando pelo `softphoneBus`. O áudio sai pela caixa de som do computador de
 * qualquer jeito, então qual aba está segurando é invisível para quem usa.
 *
 * ⛔ NÃO fazer cada aba registrar de novo "para garantir" — é exatamente o bug.
 */
export function SoftphoneProvider({ children, enabled = true }: { children: ReactNode; enabled?: boolean }) {
  const uaRef = useRef<any>(null);
  const sessionRef = useRef<any>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mutedRef = useRef(false);
  const domainRef = useRef("discadora.primetecia.com.br");
  const registeredRef = useRef(false);
  /** Já avisamos que o ramal caiu? Zera quando ele volta — ver `avisarQuedaDeRegistro`. */
  const avisouQuedaRef = useRef(false);

  const [status, setStatus] = useState<SoftphoneStatus>("carregando");
  const [open, setOpen] = useState(false);
  const [motorSalaAtiva, setMotorSalaAtiva] = useState(false);
  const [inCall, setInCall] = useState(false);
  const [encerrando, setEncerrando] = useState(false);
  const encerrandoTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [muted, setMuted] = useState(false);
  const [number, setNumber] = useState("");
  const [contactName, setContactName] = useState<string | null>(null);
  const [contactFoto, setContactFoto] = useState<string | null>(null);
  const [calling, setCalling] = useState(false);
  const [ramal, setRamal] = useState<string | null>(null);
  const [ramais, setRamais] = useState<RamalInterno[] | null>(null);
  const [historico, setHistorico] = useState<Gravacao[] | null>(null);
  const [presenca, setPresenca] = useState<string[]>([]);
  const ramaisRef = useRef<RamalInterno[] | null>(null);

  // ── Coordenação entre abas ────────────────────────────────────────────────
  const [souDono, setSouDono] = useState(false);
  const souDonoRef = useRef(false);
  const canalRef = useRef<BroadcastChannel | null>(null);
  const estadoRef = useRef<SoftphoneEstado | null>(null);
  const executarRef = useRef<((cmd: SoftphoneCmd) => void) | null>(null);
  const ultimaNoticiaRef = useRef(0);
  const incomingAnteriorRef = useRef(false);
  const liberarPosseRef = useRef<(() => void) | null>(null);

  // ── Reconexão do registro ─────────────────────────────────────────────────
  const desmontandoRef = useRef(false);
  const reregTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tentativaRef = useRef(0);

  const carregarPresenca = useCallback(() => {
    getPresenca().then((r) => setPresenca(r || [])).catch(() => {});
  }, []);
  const [timer, setTimer] = useState("00:00");
  const [incoming, setIncoming] = useState(false);
  const [incomingFrom, setIncomingFrom] = useState<string | null>(null);
  const ramaisLoadedRef = useRef(false);

  /** Popup da discadora. Sem coordenação entre abas ele não pode registrar (chutaria a principal). */
  const ehPopup = typeof window !== "undefined" && window.location.pathname.startsWith("/discadora-popup");
  const podeSerDono = enabled && !(ehPopup && !COORDENA_ABAS);
  const [rodadaPosse, setRodadaPosse] = useState(0);
  /** Quem pediu o último comando por outra aba — é para ELA que a falha tem que aparecer. */
  const cmdDeFora = useRef(false);
  const ultimoCmdEmRef = useRef(0);

  // Destrava o áudio + pede permissão de notificação no 1º gesto do usuário.
  useEffect(() => {
    const unlock = () => {
      sfAudio.unlock();
      try {
        if (typeof Notification !== "undefined" && Notification.permission === "default") {
          Notification.requestPermission().catch(() => {});
        }
      } catch { /* noop */ }
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  // Notificação do navegador quando chega chamada e a aba NÃO está visível.
  // Só a aba dona notifica — senão o consultor com 5 abas recebe 5 avisos do mesmo toque.
  useEffect(() => {
    if (!incoming || !souDono) return;
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    if (typeof document !== "undefined" && !document.hidden) return;
    let n: Notification | null = null;
    try {
      const match = ramaisRef.current?.find((r) => r.ramal === incomingFrom);
      const nome = match?.nome || (incomingFrom ? `Ramal ${incomingFrom}` : "Chamada recebida");
      n = new Notification(`📞 ${nome}`, { body: "Chamada recebida — Softphone Prime", icon: match?.foto || undefined, tag: "softphone-incoming", requireInteraction: true } as NotificationOptions);
      n.onclick = () => { try { window.focus(); } catch { /* noop */ } n?.close(); };
    } catch { /* noop */ }
    return () => { try { n?.close(); } catch { /* noop */ } };
  }, [incoming, incomingFrom, souDono]);

  // Marimba enquanto RECEBE chamada. Só a dona toca: são as mesmas caixas de som,
  // e N abas tocando ao mesmo tempo viram ruído sobreposto.
  useEffect(() => {
    if (incoming && souDono) sfAudio.startRingtone();
    else sfAudio.stopRingtone();
    return () => sfAudio.stopRingtone();
  }, [incoming, souDono]);

  // Tom de "chamando…" enquanto LIGA (antes de conectar). Mesma regra do ringtone.
  useEffect(() => {
    if (calling && souDono) sfAudio.startRingback();
    else sfAudio.stopRingback();
    return () => sfAudio.stopRingback();
  }, [calling, souDono]);

  // Pisca o título em TODAS as abas — é onde a pessoa vê que o telefone tocou,
  // esteja ela em qual aba estiver.
  useEffect(() => {
    if (!incoming) return;
    const original = document.title;
    let on = false;
    const iv = setInterval(() => {
      on = !on;
      document.title = on ? "📞 Chamada recebida…" : original;
    }, 900);
    return () => { clearInterval(iv); document.title = original; };
  }, [incoming]);

  const carregarRamais = useCallback(() => {
    if (ramaisLoadedRef.current) return;
    ramaisLoadedRef.current = true;
    getRamaisInternos()
      .then((r) => { ramaisRef.current = r || []; setRamais(r || []); })
      .catch(() => { ramaisLoadedRef.current = false; setRamais([]); });
  }, []);

  const carregarHistorico = useCallback(() => {
    setHistorico(null);
    getGravacoes({ dias: 60, limit: 200 })
      .then((r) => setHistorico(r || []))
      .catch(() => setHistorico([]));
  }, []);

  const urlGravacao = useCallback(async (id: string): Promise<string | null> => {
    try { const r = await getGravacaoUrl(id); return r?.url ?? null; } catch { return null; }
  }, []);

  const attachRemote = useCallback((pc: any) => {
    if (!pc || !audioRef.current) return;
    const tracks = pc.getReceivers().map((r: any) => r.track).filter((t: any) => t && t.kind === "audio");
    if (!tracks.length) return;
    audioRef.current.srcObject = new MediaStream(tracks);
    audioRef.current.play().catch(() => {});
  }, []);

  const startTimer = useCallback(() => {
    let s = 0;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      s++;
      const m = Math.floor(s / 60), r = s % 60;
      setTimer(`${m < 10 ? "0" : ""}${m}:${r < 10 ? "0" : ""}${r}`);
    }, 1000);
  }, []);

  const endCall = useCallback(() => {
    setInCall(false);
    setEncerrando(false);
    if (encerrandoTimeoutRef.current) { clearTimeout(encerrandoTimeoutRef.current); encerrandoTimeoutRef.current = null; }
    setCalling(false);
    setMuted(false);
    mutedRef.current = false;
    setIncoming(false);
    setIncomingFrom(null);
    setContactFoto(null);
    setTimer("00:00");
    sessionRef.current = null;
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  // ── O canal entre as abas ─────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;
    const ch = criarCanal();
    canalRef.current = ch;
    return () => {
      canalRef.current = null;
      try { ch?.close(); } catch { /* noop */ }
    };
  }, [enabled]);

  /** Manda um comando para a aba dona executar. */
  const mandarCmd = useCallback((cmd: SoftphoneCmd): boolean => {
    ultimoCmdEmRef.current = Date.now();
    try {
      canalRef.current?.postMessage({ t: "cmd", cmd } satisfies SoftphoneMsg);
      return !!canalRef.current;
    } catch {
      return false;
    }
  }, []);

  /**
   * Falha de uma ação tem que aparecer na janela em que a pessoa CLICOU. Se o
   * clique veio de outra aba, a dona repassa para ela em vez de mostrar aqui,
   * onde ninguém está olhando. Toda falha também vai para o log do servidor.
   */
  const avisarFalhaAcao = useCallback((acao: string, titulo: string, descricao?: string, motivo?: unknown) => {
    registrarFalhaSoftphone({
      ramal: estadoRef.current?.ramal ?? null,
      acao,
      motivo: `${titulo}${motivo ? ` · ${String((motivo as { name?: string })?.name ?? motivo)}` : ""}`,
      janela: ehMesa ? "mesa" : window.location.pathname.startsWith("/discadora-popup") ? "popup" : "crm",
      dona: souDonoRef.current,
    });
    if (souDonoRef.current && cmdDeFora.current) {
      try { canalRef.current?.postMessage({ t: "falha", titulo, descricao } satisfies SoftphoneMsg); } catch { /* noop */ }
      return;
    }
    toast.error(titulo, { description: descricao, duration: 12000 });
  }, []);

  // ── Quem segura o registro ────────────────────────────────────────────────
  // Com a Mesa aberta, ELA é a dona: o atender e o microfone ficam na janela em
  // que a pessoa clica. As outras abas saem da frente e voltam para a fila — quando
  // a Mesa fecha, o navegador devolve a posse para o CRM sozinho.
  useEffect(() => {
    if (!podeSerDono) return;
    let passarAVez: ReturnType<typeof setTimeout> | null = null;
    const soltar = disputarPosse(() => {
      if (!ehMesa && mesaEstaAberta()) {
        soltar();
        passarAVez = setTimeout(() => setRodadaPosse((n) => n + 1), 1500);
        return;
      }
      souDonoRef.current = true;
      setSouDono(true);
    });
    liberarPosseRef.current = soltar;
    return () => {
      if (passarAVez) clearTimeout(passarAVez);
      souDonoRef.current = false;
      setSouDono(false);
      if (liberarPosseRef.current === soltar) liberarPosseRef.current = null;
      soltar();
    };
  }, [podeSerDono, rodadaPosse]);

  // A aba do CRM que é dona cede para a Mesa — nunca no meio de uma chamada.
  useEffect(() => {
    if (!souDono || ehMesa || !COORDENA_ABAS) return;
    const iv = setInterval(() => {
      if (!mesaEstaAberta() || sessionRef.current) return;
      setRodadaPosse((n) => n + 1);
    }, 2000);
    return () => clearInterval(iv);
  }, [souDono]);

  // ── O softphone de verdade: só existe na aba dona ─────────────────────────
  useEffect(() => {
    if (!enabled || !souDono) return;
    desmontandoRef.current = false;
    tentativaRef.current = 0;

    // Esta aba pode estar herdando a posse de uma dona que morreu no meio de uma
    // ligação. O que ela estava espelhando é passado — quem assume começa limpo,
    // senão a tela mostraria "em ligação" sem existir ligação nenhuma.
    endCall();

    const audio = document.createElement("audio");
    audio.autoplay = true;
    document.body.appendChild(audio);
    audioRef.current = audio;

    let cancelled = false;

    /**
     * Volta a registrar sozinho. Antes disto, `unregistered` não mexia no status:
     * a aba ficava VERDE sem registro nenhum, a pessoa apertava ligar e não saía
     * nada — e a recuperação só vinha no ciclo seguinte do JsSIP (600s).
     */
    const agendarReregistro = (msImediato?: number) => {
      if (desmontandoRef.current || cancelled) return;
      if (reregTimerRef.current) clearTimeout(reregTimerRef.current);
      const n = tentativaRef.current++;
      const espera = msImediato ?? Math.min(3000 * 2 ** n, 30_000);
      reregTimerRef.current = setTimeout(() => {
        if (desmontandoRef.current || cancelled) return;
        try { uaRef.current?.register(); } catch { /* a próxima tentativa cobre */ }
      }, espera);
    };

    /**
     * Perder o ramal era MUDO: os handlers abaixo só trocavam o `status`, e o
     * único sinal para quem usa era um pontinho colorido de 13px no botão
     * flutuante. O aviso de verdade só existia reativamente dentro do `call()`
     * — ou seja, a pessoa só descobria que estava sem linha DEPOIS de tentar
     * ligar para um cliente e falhar (Douglas 04/09/2026).
     *
     * Duas travas contra spam de toast, porque o JsSIP reconecta sozinho e
     * `disconnected`/`unregistered`/`registrationFailed` repetem a cada
     * tentativa:
     *  - `estavaRegistrado`: no boot e no 1º registro não houve queda nenhuma,
     *    então não se avisa nada (falha de credencial já é dita pelo `call()`);
     *  - `avisouQuedaRef`: um aviso por queda. Só volta a valer depois que o
     *    ramal registrar de novo.
     */
    const avisarQuedaDeRegistro = (estavaRegistrado: boolean) => {
      if (desmontandoRef.current || cancelled) return;
      if (!estavaRegistrado || avisouQuedaRef.current) return;
      avisouQuedaRef.current = true;
      toast.error("Você está sem linha — ligações não vão sair", {
        description: "O softphone está reconectando sozinho. Espere o ponto ficar verde antes de ligar.",
        duration: 9000,
      });
    };

    const startUA = (cfg: { endpoint: string; secret: string; wss: string; domain: string }) => {
      try {
        const JsSIP = (window as any).JsSIP;
        if (!JsSIP) { setStatus("falha"); return; }
        domainRef.current = cfg.domain;
        const sock = new JsSIP.WebSocketInterface(cfg.wss);
        const ua = new JsSIP.UA({ sockets: [sock], uri: `sip:${cfg.endpoint}@${cfg.domain}`, password: cfg.secret, register: true, session_timers: false });
        uaRef.current = ua;
        ua.on("connecting", () => setStatus("conectando"));
        ua.on("disconnected", () => {
          const estavaRegistrado = registeredRef.current;
          registeredRef.current = false;
          setStatus("desconectado");
          avisarQuedaDeRegistro(estavaRegistrado);
        });
        ua.on("registered", () => {
          tentativaRef.current = 0;
          registeredRef.current = true;
          setStatus("registrado");
          // Só anuncia a volta para quem viu a queda — senão todo boot daria um
          // "linha de volta" sem que linha nenhuma tivesse caído.
          if (avisouQuedaRef.current) {
            avisouQuedaRef.current = false;
            if (!desmontandoRef.current && !cancelled) toast.success("Linha de volta — já dá para ligar");
          }
        });
        // Perder o registro tem que APARECER na hora e voltar na hora.
        ua.on("unregistered", () => {
          const estavaRegistrado = registeredRef.current;
          registeredRef.current = false;
          if (desmontandoRef.current || cancelled) return;
          setStatus("desconectado");
          avisarQuedaDeRegistro(estavaRegistrado);
          agendarReregistro(1000);
        });
        ua.on("registrationFailed", () => {
          const estavaRegistrado = registeredRef.current;
          registeredRef.current = false;
          if (desmontandoRef.current || cancelled) return;
          setStatus("falha");
          avisarQuedaDeRegistro(estavaRegistrado);
          agendarReregistro();
        });
        ua.on("newRTCSession", (e: any) => {
          const session = e.session;
          // Uma sessão nova só assume o softphone se ele não estiver ocupado.
          if (sessionRef.current && sessionRef.current !== session) {
            try { session.terminate(); } catch { /* noop */ }
            return;
          }
          sessionRef.current = session;
          // Precisa viver FORA do if: o handler de "confirmed" abaixo usa isto e
          // roda nas duas direcoes. Chamada que SAI nao tem sala nenhuma — e' false.
          let salaDiscadora = false;
          if (e.originator === "remote") {
            const from = session.remote_identity?.uri?.user || session.remote_identity?.display_name || "";
            const displayName = String(session.remote_identity?.display_name || "");
            salaDiscadora = from === "7000" || /sala da discadora/i.test(displayName);
            setIncomingFrom(from || null);
            setNumber(from || "");
            setIncoming(true);
            if (salaDiscadora) setMotorSalaAtiva(true);
            setOpen(!salaDiscadora || ehPopup || !mesaEstaAberta());
            // Quem está ligando: ramal interno primeiro, senão procura no CRM.
            const interno = ramaisRef.current?.find((r) => r.ramal === from) || null;
            setContactName(salaDiscadora ? "Sala da Discadora — atenda uma vez" : (interno?.nome ?? null));
            setContactFoto(interno?.foto ?? null);
            if (!salaDiscadora && !interno && from) {
              identificarNumero(from)
                .then((achado) => { if (achado) setContactName(achado); })
                .catch(() => { /* fica só o número */ });
            }
          }
          session.on("peerconnection", (ev: any) => {
            ev.peerconnection.addEventListener("track", () => attachRemote(ev.peerconnection));
          });
          session.on("accepted", () => setIncoming(false));
          /* O JsSIP chama isto DENTRO do .then() do setRemoteDescription: se
             estourar aqui, ele cai no .catch() e trata como "Bad Media
             Description" — manda BYE 488 e a ligacao MORRE no instante em que a
             pessoa atende, sem nada no console.
             Foi o que aconteceu de 27/08 a 01/09/2026: `salaDiscadora` so existia
             no ramo de chamada que ENTRA, e o `if` abaixo estourava ReferenceError
             em toda chamada que SAI. Toda ligacao manual da empresa caiu por 5
             dias — e a culpa parecia da operadora. O try/catch existe pra que erro
             de tela nunca mais derrube telefone. */
          session.on("confirmed", () => {
            try {
              setIncoming(false);
              setCalling(false);
              setInCall(true);
              setMuted(false);
              if (salaDiscadora) setOpen(false);
              attachRemote(session.connection);
              startTimer();
            } catch (err) {
              console.error("[softphone] erro ao confirmar a chamada (a ligacao segue de pe):", err);
            }
          });
          session.on("ended", endCall);
          session.on("failed", endCall);
        });
        ua.start();
      } catch { setStatus("falha"); }
    };

    const loadJsSIP = () => new Promise<void>((resolve, reject) => {
      if ((window as any).JsSIP) return resolve();
      const sc = document.createElement("script");
      sc.src = "/vendor/jssip.min.js";
      sc.async = true;
      sc.onload = () => resolve();
      sc.onerror = () => reject(new Error("jssip load"));
      document.body.appendChild(sc);
    });

    const boot = async () => {
      setStatus("carregando");
      try {
        await loadJsSIP();
        if (cancelled) return;
        const cfg = await getMeuSip();
        if (cancelled) return;
        if (!cfg) { setStatus("sem-ramal"); return; }
        setRamal(cfg.endpoint);
        setStatus("conectando");
        startUA(cfg);
      } catch {
        if (!cancelled) setStatus("falha");
      }
    };
    boot();

    /**
     * Aba que volta do segundo plano e computador que volta da rede: o navegador
     * congela os timers em aba escondida, e o registro pode ter vencido enquanto
     * isso. Conferir na volta é mais barato que esperar o próximo ciclo.
     */
    const conferirRegistro = () => {
      if (desmontandoRef.current || cancelled) return;
      if (document.hidden) return;
      if (registeredRef.current) return;
      if (!uaRef.current) return;
      tentativaRef.current = 0;
      agendarReregistro(300);
    };
    document.addEventListener("visibilitychange", conferirRegistro);
    window.addEventListener("online", conferirRegistro);
    window.addEventListener("focus", conferirRegistro);

    return () => {
      cancelled = true;
      desmontandoRef.current = true;
      document.removeEventListener("visibilitychange", conferirRegistro);
      window.removeEventListener("online", conferirRegistro);
      window.removeEventListener("focus", conferirRegistro);
      if (reregTimerRef.current) clearTimeout(reregTimerRef.current);
      registeredRef.current = false;
      try { uaRef.current?.stop(); } catch { /* noop */ }
      uaRef.current = null;
      if (timerRef.current) clearInterval(timerRef.current);
      audioRef.current?.remove();
      audioRef.current = null;
    };
  }, [enabled, souDono, attachRemote, startTimer, endCall, ehPopup]);

  // ── A dona publica o estado; as outras espelham ───────────────────────────
  useEffect(() => {
    const estado: SoftphoneEstado = { status, ramal, inCall, encerrando, calling, incoming, incomingFrom, number, contactName, contactFoto, muted, timer };
    estadoRef.current = estado;
    if (!enabled || !souDono) return;
    try { canalRef.current?.postMessage({ t: "estado", estado } satisfies SoftphoneMsg); } catch { /* noop */ }
  }, [enabled, souDono, status, ramal, inCall, encerrando, calling, incoming, incomingFrom, number, contactName, contactFoto, muted, timer]);

  // Batida de vida: sem isso, uma aba aberta com o softphone parado nunca saberia
  // que a dona continua de pé.
  useEffect(() => {
    if (!enabled || !souDono) return;
    const iv = setInterval(() => {
      if (!estadoRef.current) return;
      try { canalRef.current?.postMessage({ t: "estado", estado: estadoRef.current } satisfies SoftphoneMsg); } catch { /* noop */ }
    }, BATIDA_MS);
    return () => clearInterval(iv);
  }, [enabled, souDono]);

  // ── Escuta: estado da dona e comandos das outras ──────────────────────────
  useEffect(() => {
    if (!enabled) return;
    const ch = canalRef.current;
    if (!ch) return;

    const aoReceber = (ev: MessageEvent) => {
      const m = ev.data as SoftphoneMsg | undefined;
      if (!m?.t) return;

      if (m.t === "ola") {
        // Aba nova entrou: a dona responde na hora para ela não ficar amarela à toa.
        if (souDonoRef.current && estadoRef.current) {
          try { ch.postMessage({ t: "estado", estado: estadoRef.current } satisfies SoftphoneMsg); } catch { /* noop */ }
        }
        return;
      }

      if (m.t === "falha") {
        if (souDonoRef.current || Date.now() - ultimoCmdEmRef.current > 60_000) return;
        toast.error(m.titulo, { description: m.descricao, duration: 12000 });
        return;
      }

      if (m.t === "cmd") {
        if (souDonoRef.current) executarRef.current?.(m.cmd);
        return;
      }

      if (m.t === "estado") {
        if (souDonoRef.current) return; // a dona é a fonte, não espelha ninguém
        ultimaNoticiaRef.current = Date.now();
        const e = m.estado;
        setStatus(e.status as SoftphoneStatus);
        setRamal(e.ramal);
        setInCall(e.inCall);
        setEncerrando(e.encerrando);
        setCalling(e.calling);
        setIncoming(e.incoming);
        setIncomingFrom(e.incomingFrom);
        setMuted(e.muted);
        setTimer(e.timer);
        // Número e contato só são espelhados durante uma chamada: fora dela, cada
        // aba tem o seu teclado e ninguém quer o que o outro está digitando.
        if (e.calling || e.inCall || e.incoming) {
          setNumber(e.number);
          setContactName(e.contactName);
          setContactFoto(e.contactFoto);
        }
        // Abre o painel quando o telefone COMEÇA a tocar, não a cada batida —
        // senão o painel voltaria sozinho depois de a pessoa fechar.
        const salaDiscadora = e.number.replace(/\D/g, "") === "7000"
          || e.contactName?.toLocaleLowerCase("pt-BR").includes("sala da discadora");
        if (salaDiscadora && (e.incoming || e.inCall)) setMotorSalaAtiva(true);
        if (salaDiscadora && e.inCall) setOpen(false);
        else if (e.incoming && !incomingAnteriorRef.current) {
          setOpen(!salaDiscadora || ehPopup || !mesaEstaAberta());
        }
        incomingAnteriorRef.current = e.incoming;
      }
    };

    ch.addEventListener("message", aoReceber);
    try { ch.postMessage({ t: "ola" } satisfies SoftphoneMsg); } catch { /* noop */ }
    return () => ch.removeEventListener("message", aoReceber);
  }, [enabled, ehPopup]);

  // Dona sumiu (aba travada, por exemplo): não deixar verde mentiroso na tela.
  useEffect(() => {
    if (!enabled || souDono || !COORDENA_ABAS) return;
    const conferirDono = () => {
      if (souDonoRef.current || document.hidden) return;
      if (ultimaNoticiaRef.current && Date.now() - ultimaNoticiaRef.current > ESTADO_VENCE_MS) {
        setStatus("conectando");
      }
      try { canalRef.current?.postMessage({ t: "ola" } satisfies SoftphoneMsg); } catch { /* noop */ }
    };
    const iv = setInterval(conferirDono, 4000);
    document.addEventListener("visibilitychange", conferirDono);
    window.addEventListener("focus", conferirDono);
    window.addEventListener("online", conferirDono);
    conferirDono();
    return () => {
      clearInterval(iv);
      document.removeEventListener("visibilitychange", conferirDono);
      window.removeEventListener("focus", conferirDono);
      window.removeEventListener("online", conferirDono);
    };
  }, [enabled, souDono]);

  // ── Ações da aba dona (só ela toca no JsSIP) ──────────────────────────────
  const discarLocal = useCallback((numero: string, nome?: string, foto?: string | null) => {
    const dest = normalize(numero);
    if (!dest) return;
    // O clique pode ter pegado a fresta entre perder o registro e a tela saber
    // disso. Aqui é o último cheque — e ele precisa DESFAZER o "chamando…", ou a
    // tela fica chamando para sempre uma ligação que nunca saiu.
    if (!uaRef.current || !registeredRef.current) {
      console.warn("[softphone] pedido de ligação com o ramal fora do ar — reconectando");
      setCalling(false);
      setInCall(false);
      try { uaRef.current?.register(); } catch { /* noop */ }
      avisarFalhaAcao("ligar", "A ligação NÃO foi feita — o ramal caiu neste instante",
        "O softphone está voltando sozinho. Tente de novo em alguns segundos.");
      return;
    }
    // Resolve nome/foto do ramal interno se não vieram explícitos.
    const match = ramaisRef.current?.find((r) => r.ramal === numero || r.ramal === dest) || null;
    const nomeConhecido = nome ?? match?.nome ?? null;
    setContactName(nomeConhecido);
    setContactFoto(foto ?? match?.foto ?? null);

    // Sem nome? Descobre pelo número enquanto a chamada completa (Douglas 06/08:
    // "apertei para ligar e agora não sei qual eu tinha ligado"). Não atrasa a
    // discagem — a busca corre em paralelo e só preenche o rótulo quando volta.
    if (!nomeConhecido) {
      identificarNumero(numero)
        .then((achado) => { if (achado) setContactName(achado); })
        .catch(() => { /* sem nome é o comportamento antigo */ });
    }
    setNumber(numero);
    setCalling(true);
    setInCall(false);
    uaRef.current.call(`sip:${dest}@${domainRef.current}`, {
      mediaConstraints: { audio: AUDIO_CONSTRAINTS, video: false },
      pcConfig: PC_CONFIG,
    });
  }, [avisarFalhaAcao]);

  /**
   * ATENDER precisa falar quando não dá. Sem permissão de microfone o answer do
   * JsSIP morre calado: o popup sumia na hora e a ligação seguia tocando do outro
   * lado até estourar os 30s do Dial — quem clicou jurava ter atendido. (Douglas
   * 24/08/2026, teste do Bora Fazer: a Tatiane apertou atender e a chamada caiu
   * como NO ANSWER com 30s cravados.) Pedimos a mídia ANTES para a falha virar
   * aviso na tela, e o popup fica de pé para ela liberar e atender de novo.
   */
  const atenderLocal = useCallback(async () => {
    const s = sessionRef.current;
    if (!s) {
      avisarFalhaAcao("atender", "Essa chamada já tinha caído quando você atendeu");
      return;
    }
    try {
      const teste = await navigator.mediaDevices.getUserMedia({ audio: AUDIO_CONSTRAINTS });
      teste.getTracks().forEach((t) => t.stop());
    } catch (err) {
      avisarFalhaAcao("atender", "O navegador não liberou o microfone",
        "Clique no cadeado ao lado do endereço, marque Permitir e atenda de novo.", err);
      return;
    }
    try {
      s.answer({ mediaConstraints: { audio: AUDIO_CONSTRAINTS, video: false }, pcConfig: PC_CONFIG });
    } catch (err) {
      avisarFalhaAcao("atender", "Não consegui atender essa chamada", "Recarregue a página e tente de novo.", err);
      return;
    }
    setIncoming(false);
  }, [avisarFalhaAcao]);

  /**
   * Recusar tem que dizer quando não recusou: a tela limpa o `incoming` de
   * qualquer jeito, então um `terminate()` que falhou deixava o telefone
   * tocando do outro lado com a tela já limpa aqui (Douglas 04/09/2026).
   */
  const recusarLocal = useCallback(() => {
    try {
      sessionRef.current?.terminate();
    } catch (err) {
      console.error("[softphone] recusar falhou — a chamada pode seguir tocando:", err);
      avisarFalhaAcao("recusar", "Não consegui recusar essa chamada",
        "Se o telefone continuar tocando, recarregue a página.", err);
    }
    setIncoming(false);
    setIncomingFrom(null);
  }, [avisarFalhaAcao]);

  /**
   * TRANSFERIR era 100% mudo: um `s.refer(...)` dentro de um try/catch vazio,
   * sem log e sem toast, nem no sucesso nem na falha. Se o REFER fosse recusado
   * (ramal ocupado, alvo inválido), o consultor achava que tinha passado a
   * ligação, desligava, e O CLIENTE FICAVA NA MÃO — sem rastro nenhum para
   * investigar depois (Douglas 04/09/2026).
   *
   * São TRÊS caminhos de falha, e o código antigo engolia os três:
   *  - exceção síncrona (alvo inválido);
   *  - `refer()` devolvendo `false`, sem lançar nada, quando a sessão ainda não
   *    está confirmada — conferido no bundle do JsSIP;
   *  - o REFER sair e ser recusado depois (`requestFailed` / `failed`).
   *
   * ⚠️ `requestSucceeded` é só o 202 do REFER (o pedido foi aceito), NÃO é a
   * transferência concluída. Quem prova que o cliente andou é o `accepted`, que
   * vem do NOTIFY com status 2xx — por isso o sucesso é anunciado só ali.
   */
  const transferirLocal = useCallback((target: string) => {
    const s = sessionRef.current;
    if (!s) return;
    const dest = normalize(target);
    if (!dest) return;

    let avisado = false;
    const avisarFalha = (motivo: unknown) => {
      console.error("[softphone] transferência NÃO foi feita:", motivo);
      if (avisado) return; // requestFailed e failed podem vir os dois
      avisado = true;
      toast.error("A transferência NÃO foi feita — a ligação continua com você", {
        description: "O ramal de destino não aceitou. Avise o cliente e tente outro ramal.",
        duration: 9000,
      });
    };

    // ⛔ Os handlers vão nas OPTIONS do refer(), não em `sub.on(...)` depois.
    // Parece detalhe e não é: quando o WebSocket está caído, `refer()` falha
    // DENTRO dele mesmo, de forma síncrona — a cadeia sendRefer → sendRequest →
    // RequestSender → NonInviteClientTransaction.send() chama onTransportError()
    // e emite `requestFailed` ANTES de `refer()` retornar. Assinando depois, o
    // ouvinte chega tarde e o evento cai no vazio: nem toast, nem log — de novo
    // a falha muda que este conserto veio corrigir. O próprio JsSIP dá a saída:
    // `sendRefer` registra `options.eventHandlers` antes de mandar o REFER
    // (conferido no bundle em public/vendor/jssip.min.js).
    // ⚠️ Cenário real, não teórico: a sessão WebRTC segue em STATUS_CONFIRMED com
    // áudio fluindo mesmo com o WSS caído (a mídia não passa pelo WebSocket).
    // Tipos minimos: o JsSIP aqui e' bundle vendorizado (public/vendor), sem .d.ts.
    const eventHandlers = {
      requestFailed: (e?: { cause?: unknown }) => avisarFalha(e?.cause ?? e),
      failed: (e?: { status_line?: unknown }) => avisarFalha(e?.status_line ?? e),
      accepted: () => {
        if (avisado) return;
        avisado = true;
        toast.success("Transferência concluída");
      },
    };

    let sub: unknown;
    try {
      sub = s.refer(`sip:${dest}@${domainRef.current}`, { eventHandlers });
    } catch (err) {
      avisarFalha(err);
      return;
    }
    if (!sub) {
      avisarFalha(`refer() devolveu ${String(sub)} — sessão não está confirmada`);
    }
  }, []);

  /**
   * DESLIGAR precisa funcionar sempre — é o botão que tira a pessoa da ligação.
   *
   * Antes era `try { session?.terminate() } catch {}`: qualquer falha morria em
   * silêncio, sem nada no console, e a tela ficava "em ligação" para sempre.
   * Agora a falha aparece no log e a chamada é encerrada na marra (a UI volta ao
   * normal e o áudio é solto), para nunca prender ninguém numa ligação fantasma.
   */
  const desligarLocal = useCallback(() => {
    const emSalaDiscadora = motorSalaAtiva
      || number.replace(/\D/g, "") === "7000"
      || contactName?.toLocaleLowerCase("pt-BR").includes("sala da discadora");
    if (emSalaDiscadora) {
      desligarClienteMotorAvisando();
      return;
    }
    const s = sessionRef.current;
    if (!s) {
      console.warn("[softphone] desligar sem sessão ativa — limpando a tela");
      endCall();
      return;
    }
    // ⛔ Feedback NA HORA do clique — antes disto, `inCall` só saía quando o
    // JsSIP confirmava "ended"/"failed" (round-trip de rede), e o clique
    // parecia não ter feito nada nesse meio-tempo (Douglas 04/09/2026).
    setEncerrando(true);
    // Rede de segurança: se o "ended" nunca voltar (WS caiu bem na hora, por
    // exemplo), a tela não pode ficar travada em "encerrando" para sempre.
    if (encerrandoTimeoutRef.current) clearTimeout(encerrandoTimeoutRef.current);
    encerrandoTimeoutRef.current = setTimeout(() => {
      console.warn('[softphone] "ended" não voltou em 6s — encerrando na marra');
      endCall();
    }, 6000);
    try {
      s.terminate();
    } catch (err) {
      console.error("[softphone] terminate() falhou, encerrando na marra:", err);
      try { s.terminate({ status_code: 603 }); } catch { /* já estava morta */ }
      endCall();
    }
  }, [contactName, endCall, motorSalaAtiva, number]);

  /**
   * O MUDO não pode mentir para a tela. Antes o estado era gravado ANTES do
   * try e o `setMuted(nv)` rodava mesmo se o JsSIP tivesse explodido: o botão
   * ficava "mudo" com o áudio ainda saindo, e a pessoa falava do cliente
   * achando que ele não estava ouvindo (Douglas 04/09/2026).
   * Agora o estado só anda se o JsSIP aceitou.
   */
  const mudoLocal = useCallback(() => {
    const s = sessionRef.current;
    if (!s) return;
    const nv = !mutedRef.current;
    try {
      nv ? s.mute() : s.unmute();
    } catch (err) {
      console.error("[softphone] mudo não foi aplicado:", err);
      toast.error(nv ? "NÃO consegui te deixar mudo — o cliente ainda te ouve" : "NÃO consegui tirar o mudo — o cliente não te ouve", {
        description: "Tente de novo. Se insistir, desligue e ligue outra vez.",
        duration: 9000,
      });
      return;
    }
    mutedRef.current = nv;
    setMuted(nv);
  }, []);

  const dtmfLocal = useCallback((d: string) => {
    if (sessionRef.current) {
      // Sem log, tecla que não chegou na URA ficava indistinguível de tecla
      // digitada errado — não dava para investigar depois (Douglas 04/09/2026).
      try { sessionRef.current.sendDTMF(d); } catch (err) { console.error("[softphone] DTMF não foi enviado:", d, err); }
    }
  }, []);

  // A dona executa o que as outras abas pedirem.
  useEffect(() => {
    executarRef.current = (cmd: SoftphoneCmd) => {
      cmdDeFora.current = true;
      switch (cmd.c) {
        case "call": discarLocal(cmd.numero, cmd.nome, cmd.foto); break;
        case "hangup": desligarLocal(); break;
        case "answer": atenderLocal(); break;
        case "reject": recusarLocal(); break;
        case "mute": mudoLocal(); break;
        case "dtmf": dtmfLocal(cmd.d); break;
        case "transfer": transferirLocal(cmd.target); break;
      }
    };
  }, [discarLocal, desligarLocal, atenderLocal, recusarLocal, mudoLocal, dtmfLocal, transferirLocal]);

  // ── A API pública: igual em toda aba, dona ou não ─────────────────────────

  /**
   * LIGAÇÃO MANUAL É EXCLUSIVA DO SOFTPHONE DA PRIME (Douglas 06/08: "eu quero
   * o uso manual exclusivo do nosso softphone, não quero deixar externo").
   *
   * ⛔ NÃO reintroduzir `window.open('tel:')` aqui. Era o que existia até 06/08:
   * com o softphone desconectado, o clique abria o discador do computador SEM
   * AVISAR. A pessoa falava com o cliente achando que tinha ligado pelo CRM —
   * e a ligação saía sem gravação e sem registro.
   */
  const call = useCallback((numero: string, nome?: string, foto?: string | null) => {
    const dest = normalize(numero);
    if (!dest) return;

    if (status !== "registrado") {
      setOpen(true); // abre o painel para a pessoa ver o status do próprio ramal
      toast.error("A ligação NÃO foi feita — softphone desconectado", {
        description:
          status === "sem-ramal"
            ? "Você está sem ramal configurado no CRM. Fale com o suporte para liberar o seu."
            : "O softphone está reconectando sozinho. Tente de novo em alguns segundos — ligar por fora do CRM não fica gravado.",
        duration: 9000,
      });
      return;
    }

    // Mostra a ligação já nesta aba: quem apertou o botão vê o que está acontecendo,
    // mesmo que o áudio esteja sendo tocado por outra.
    setNumber(numero);
    if (nome) setContactName(nome);
    if (foto !== undefined) setContactFoto(foto);
    setCalling(true);
    setInCall(false);
    setOpen(true);

    if (souDonoRef.current) { cmdDeFora.current = false; discarLocal(numero, nome, foto); return; }
    if (!mandarCmd({ c: "call", numero, nome, foto })) {
      setCalling(false);
      toast.error("A ligação NÃO foi feita — não consegui falar com o softphone", {
        description: "Recarregue a página (Ctrl+Shift+R) e tente de novo.",
        duration: 9000,
      });
    }
  }, [status, discarLocal, mandarCmd]);

  const answerCall = useCallback(() => {
    if (souDonoRef.current) { cmdDeFora.current = false; atenderLocal(); return; }
    setIncoming(false);
    mandarCmd({ c: "answer" });
  }, [atenderLocal, mandarCmd]);

  const rejectCall = useCallback(() => {
    if (souDonoRef.current) { cmdDeFora.current = false; recusarLocal(); return; }
    setIncoming(false);
    setIncomingFrom(null);
    mandarCmd({ c: "reject" });
  }, [recusarLocal, mandarCmd]);

  const transfer = useCallback((target: string) => {
    if (souDonoRef.current) { transferirLocal(target); return; }
    mandarCmd({ c: "transfer", target });
  }, [transferirLocal, mandarCmd]);

  const hangup = useCallback(() => {
    const emSalaDiscadora = motorSalaAtiva
      || number.replace(/\D/g, "") === "7000"
      || contactName?.toLocaleLowerCase("pt-BR").includes("sala da discadora");
    if (emSalaDiscadora) {
      desligarClienteMotorAvisando();
      return;
    }
    if (souDonoRef.current) { desligarLocal(); return; }
    mandarCmd({ c: "hangup" });
  }, [contactName, desligarLocal, mandarCmd, motorSalaAtiva, number]);

  const toggleMute = useCallback(() => {
    if (souDonoRef.current) { mudoLocal(); return; }
    mandarCmd({ c: "mute" });
  }, [mudoLocal, mandarCmd]);

  const sendDTMF = useCallback((d: string) => {
    if (inCall) {
      if (souDonoRef.current) dtmfLocal(d);
      else mandarCmd({ c: "dtmf", d });
    }
    setNumber((n) => n + d);
  }, [inCall, dtmfLocal, mandarCmd]);

  const api: SoftphoneApi = {
    status, open, setOpen, inCall, encerrando, muted, number, setNumber, contactName, contactFoto, calling, ramal, timer, ramais, carregarRamais,
    presenca, carregarPresenca, historico, carregarHistorico, urlGravacao,
    incoming, incomingFrom, answerCall, rejectCall, call, transfer, hangup, toggleMute, sendDTMF,
    motorSalaAtiva, setMotorSalaAtiva,
  };

  return <SoftphoneContext.Provider value={enabled ? api : null}>{children}</SoftphoneContext.Provider>;
}
